// Offline data layer for the Dekrbat PSEQ2 oracle app.
// Weather per zone is downloaded in the background (past 7 days + 8 days forecast)
// and persisted in localStorage so graphs, constats and PDF reports work offline.

const CACHE_KEY = "dekrbat_zone_cache_v1";
const ZONES_KEY = "dekrbat_zone_list_v1";

export const DEFAULT_ZONES = [
  { id: "la-union", name: "La Unión", lat: 13.3369, lng: -87.8439, origin: "default" },
  { id: "jiquilisco", name: "Jiquilisco", lat: 13.3269, lng: -88.5692, origin: "default" },
  { id: "acajutla", name: "Acajutla", lat: 13.5926, lng: -89.8277, origin: "default" },
  { id: "puerto-el-triunfo", name: "Puerto El Triunfo", lat: 13.2833, lng: -88.55, origin: "default" },
  { id: "la-libertad", name: "La Libertad", lat: 13.4883, lng: -89.3222, origin: "default" },
  { id: "san-salvador", name: "San Salvador", lat: 13.6929, lng: -89.2182, origin: "default" },
];

export function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

export function readStoredZones() {
  try {
    return JSON.parse(localStorage.getItem(ZONES_KEY) || "null");
  } catch {
    return null;
  }
}

export function mergeZones(scenarioZones = []) {
  const stored = readStoredZones() || [];
  const merged = [...DEFAULT_ZONES];
  for (const list of [stored, scenarioZones]) {
    for (const z of list) {
      // Dedupe only near-exact duplicates (~2 km): a custom zone a few km from a default one is legitimate
      if (!merged.some((m) => Math.abs(m.lat - z.lat) < 0.02 && Math.abs(m.lng - z.lng) < 0.02)) {
        merged.push(z);
      }
    }
  }
  localStorage.setItem(ZONES_KEY, JSON.stringify(merged.filter((z) => z.origin !== "custom")));
  return merged;
}

export async function syncZone(zone) {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${zone.lat.toFixed(4)}&longitude=${zone.lng.toFixed(4)}` +
    `&current=temperature_2m,relative_humidity_2m,surface_pressure` +
    `&daily=temperature_2m_max,temperature_2m_min,sunshine_duration,precipitation_sum` +
    `&timezone=auto&past_days=7&forecast_days=8`;
  const r = await fetch(url);
  const b = await r.json();
  if (b.error || !b.current || !b.daily) throw new Error("no data");
  const entry = {
    fetchedAt: new Date().toISOString(),
    current: {
      temp: b.current.temperature_2m,
      humidity: b.current.relative_humidity_2m,
      pressure: b.current.surface_pressure,
    },
    days: b.daily.time.map((d, i) => ({
      date: d,
      tmax: b.daily.temperature_2m_max[i],
      tmin: b.daily.temperature_2m_min[i],
      sunshine: (b.daily.sunshine_duration[i] ?? 0) / 3600,
      rain: b.daily.precipitation_sum[i] ?? 0,
    })),
  };
  const cache = readCache();
  cache[zone.id] = entry;
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  return entry;
}

export async function syncAllZones(zones, onProgress) {
  let done = 0;
  for (const z of zones) {
    try {
      await syncZone(z);
    } catch {}
    done += 1;
    onProgress?.(done, zones.length);
  }
  return readCache();
}

// ---- Oracle logic: constat (real-time observation) + prediction (forecast) ----

// Default "pass" bands for the constat verdict; zones can override them server-side.
// Partial bands stay fixed (the wider historical ranges).
export const DEFAULT_THRESHOLDS = {
  temp: [26, 30],
  sunshine: 6,
  humidity: [45, 75],
  pressure: [1000, 1025],
  co2: [350, 1200],
};
const PARTIAL_BANDS = {
  temp: [22, 33],
  sunshine: 4,
  humidity: [30, 85],
  pressure: [985, 1035],
  co2Max: 1800,
};

const rangeState = (v, pass, partial) =>
  v >= pass[0] && v <= pass[1] ? "pass" : v >= partial[0] && v <= partial[1] ? "partial" : "fail";

export function constatCriteria(current, todaySunshine, telemetry = null, thresholds = null) {
  if (!current) return null;
  const th = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) };
  // Real IoT probe readings (CO2/temp/humidity/pressure) override estimated weather when fresh
  const t = telemetry?.temp ?? current.temp;
  const h = telemetry?.humidity ?? current.humidity;
  const p = telemetry?.pressure ?? current.pressure;
  const s = todaySunshine ?? 0;
  const crit = [
    { key: "temp", value: `${t.toFixed(1)}°C`, state: rangeState(t, th.temp, PARTIAL_BANDS.temp) },
    { key: "sunshine", value: `${s.toFixed(1)}h`, state: s >= th.sunshine ? "pass" : s >= PARTIAL_BANDS.sunshine ? "partial" : "fail" },
    { key: "humidity", value: `${Math.round(h)}%`, state: rangeState(h, th.humidity, PARTIAL_BANDS.humidity) },
    { key: "pressure", value: `${Math.round(p)} hPa`, state: rangeState(p, th.pressure, PARTIAL_BANDS.pressure) },
  ];
  if (telemetry?.co2_ppm != null) {
    const c = telemetry.co2_ppm;
    crit.push({ key: "co2", value: `${Math.round(c)} ppm`, state: c >= th.co2[0] && c <= th.co2[1] ? "pass" : c <= PARTIAL_BANDS.co2Max ? "partial" : "fail" });
  }
  return crit;
}

export function constatVerdict(criteria) {
  if (!criteria) return "rejected";
  if (criteria.every((c) => c.state === "pass")) return "validated";
  if (criteria.some((c) => c.state === "fail")) return "rejected";
  return "partial";
}

function dayScore(tmax, sunshine) {
  let s = 0;
  if (tmax >= 28 && tmax <= 33) s += 55;
  else if (tmax >= 24 && tmax <= 36) s += 35;
  else s += 12;
  if (sunshine >= 8) s += 45;
  else if (sunshine >= 6) s += 32;
  else if (sunshine >= 4) s += 18;
  else s += 6;
  return Math.min(100, s);
}

export function predictionDays(days) {
  // forecast days only (after today's index in the past7+forecast8 series)
  const todayIso = new Date().toISOString().slice(0, 10);
  return days
    .filter((d) => d.date >= todayIso)
    .slice(0, 7)
    .map((d) => ({ date: d.date, score: dayScore(d.tmax, d.sunshine), sunshine: d.sunshine, tmax: d.tmax }));
}

export function weeklyPointsPerPond(predDays) {
  if (!predDays.length) return 0;
  const avg = predDays.reduce((a, d) => a + d.score, 0) / predDays.length;
  // 12 pts per 100 m² pond per year at score 100
  return Math.round(((avg / 100) * 12 * 7) / 365 * 1000) / 1000;
}
